import moment from 'moment'
import firebase from 'firebase/compat/app'
import type { Event, EventSortBy } from '@Interfaces/Data/EventInterface'
import { DateInput } from '@Interfaces/Util/DateInterface'
import { firestoreLikeValueToJsDate } from './datetime'

type ParsedEvent = firebase.firestore.DocumentData & {
  id: string
  createdAt: Date | null
  date: Date | null
  endTime: Date | null
  startTime: Date | null
}

/**
 * Parses event data from Firestore document
 * @param doc - Firestore document
 * @param currentUser - Current user object (for error context)
 * @returns Parsed event object
 */
export function parseEventData(
  doc: firebase.firestore.DocumentSnapshot,
  currentUser?: { id?: string }
): ParsedEvent | undefined {
  try {
    const eventData = doc.data()
    if (!eventData) return undefined

    let createdAt: Date | null = null
    let date: Date | null = null
    let endTime: Date | null = null
    let startTime: Date | null = null

    if (eventData?.createdAt?.toDate) {
      createdAt = eventData.createdAt.toDate()
    }
    if (eventData?.date?.toDate) {
      date = eventData.date.toDate()
    }
    if (eventData?.endTime?.toDate) {
      endTime = eventData.endTime.toDate()
    }
    if (eventData?.startTime?.toDate) {
      startTime = eventData.startTime.toDate()
    }

    const event: ParsedEvent = {
      ...eventData,
      createdAt,
      date,
      endTime,
      startTime,
      id: doc.id,
    }

    return event
  } catch (e) {
    console.error('error parsing event data', e, doc.id, currentUser?.id)
    return undefined
  }
}

/**
 * Gets formatted event date string with time
 * @param event - Event object with date and time properties
 * @param short - Whether to use short format
 * @returns Formatted date string
 */
export function getEventDate(
  event: { date?: DateInput; startTime?: DateInput; endTime?: DateInput },
  short: boolean = false
): string {
  const date = event?.date ? firestoreLikeValueToJsDate(event.date).toDateString() : ''
  const startTime = event?.startTime
    ? firestoreLikeValueToJsDate(event.startTime).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: short ? 'short' : undefined,
      })
    : ''

  const endTime = event?.endTime
    ? firestoreLikeValueToJsDate(event.endTime).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : ''

  return `${date} @ ${startTime}${short ? '' : ' - ' + endTime}`
}

/**
 * Gets formatted event date without time
 * @param event - Event object with date property
 * @returns Formatted date string (e.g., "Monday, January 1")
 */
export function getEventDateWithoutTime(event: { date?: DateInput }): string {
  if (!event?.date) return ''
  const date = firestoreLikeValueToJsDate(event.date)
  return moment(date).format('dddd, MMMM D')
}

type EventCoverPhoto = string | { uri?: string } | undefined | null

export function getEventDateValue(eventDate: Event['date']): number {
  if (eventDate && typeof eventDate === 'object' && 'toDate' in eventDate && eventDate.toDate) {
    return eventDate.toDate().getTime()
  }

  return firestoreLikeValueToJsDate(eventDate).getTime()
}

export function filterEventsBySearch(events: Event[], searchTerm: string): Event[] {
  if (!searchTerm || !events?.length) return events

  const term = searchTerm.toLowerCase().trim()

  return events.filter((item) => {
    return (
      item.name?.toLowerCase().includes(term) ||
      item.address?.city?.toLowerCase().includes(term) ||
      item.address?.state?.toLowerCase().includes(term)
    )
  })
}

export function sortEventsBy(sortBy: EventSortBy, events: Event[]): Event[] {
  const results = [...events]

  if (sortBy === 'date') {
    return results.sort((a, b) => {
      const aDate = getEventDateValue(a.date)
      const bDate = getEventDateValue(b.date)
      return bDate - aDate
    })
  }

  if (sortBy === 'volunteers') {
    return results.sort((a, b) => {
      const aVolunteers = Number(a.volunteersNeeded ?? 0) || 0
      const bVolunteers = Number(b.volunteersNeeded ?? 0) || 0
      return bVolunteers - aVolunteers
    })
  }

  return results.sort((a, b) => {
    const aStatus = a.active ? 1 : 0
    const bStatus = b.active ? 1 : 0
    return bStatus - aStatus
  })
}

export function getEventCoverPhotoSource(coverPhoto?: string | null): string {
  return coverPhoto || ''
}

export function shallowEqual(objA: unknown, objB: unknown): boolean {
  if (!objA || !objB) {
    return objA === objB
  }

  const recordA = objA as Record<string, unknown>
  const recordB = objB as Record<string, unknown>

  return !Object.keys(Object.assign({}, recordA, recordB)).find(
    (key) => recordA[key] !== recordB[key]
  )
}

export function hasValidEventDraft(
  details: Partial<Event> | undefined,
  selectedCauses: string[] = [],
  coverPhotos: EventCoverPhoto[] = []
): boolean {
  const hasLocation =
    details?.mode === 'In-Person'
      ? Boolean(
          details.address?.line1 &&
            details.address?.city &&
            details.address?.state &&
            details.address?.zipCode &&
            details.latLng
        )
      : Boolean(details?.meetingInfo)

  const startTime =
    details?.startTime != null ? firestoreLikeValueToJsDate(details.startTime).getTime() : null
  const endTime =
    details?.endTime != null ? firestoreLikeValueToJsDate(details.endTime).getTime() : null
  const hasValidTimeRange =
    typeof startTime === 'number' && typeof endTime === 'number' && endTime > startTime

  return Boolean(
    hasLocation &&
      details?.name &&
      details?.about &&
      hasValidTimeRange &&
      selectedCauses.length > 0 &&
      coverPhotos.length > 0
  )
}

export function isEventDraftDirty(
  details: Partial<Event> | undefined,
  selectedEvent: Partial<Event> | undefined,
  selectedCauses: string[] = [],
  coverPhotos: EventCoverPhoto[] = []
): boolean {
  return (
    (hasValidEventDraft(details, selectedCauses, coverPhotos) &&
      !shallowEqual(selectedCauses, selectedEvent?.causes)) ||
    !shallowEqual(details, selectedEvent) ||
    !shallowEqual(coverPhotos, [selectedEvent?.coverPhoto])
  )
}
